import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { FormField, form, max, min, required, submit, validate } from '@angular/forms/signals';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog';
import {
  AppSettingsService,
  type AvailableModelSummary,
  type AntennaLobeVisualizationSettings,
  type CensusUsIngestStateResponse,
  type CellTowerDataSourcePurgeStatus,
  type JobCleanupEstimate,
  type JobCleanupResult,
  type JobCleanupRetention,
  type StageAGuardrailSettings,
  type SubscriberMarkerVisualizationSettings,
  type SimulationSettings,
} from '../../services/app-settings';
import { SionnaJobService, type JobStatus } from '../../services/sionna-job.service';

const DEFAULT_ANTENNA_LOBE_SETTINGS: AntennaLobeVisualizationSettings = {
  minimum_boresight_length_m: 18,
  maximum_boresight_length_m: 105,
  maximum_ground_touch_length_m: 650,
  minimum_power_dbm: 35,
  maximum_power_dbm: 49,
  horizontal_width_ratio: 0.42,
  vertical_width_ratio: 0.24,
  ground_clearance_m: 0.5,
};

const DEFAULT_SIMULATION_SETTINGS: SimulationSettings = {
  radio_map_frequency_ghz: 1,
  radio_map_bandwidth_hz: 10_000_000,
  radio_map_temperature_k: 290,
  radio_map_receiver_noise_figure_db: 7,
  radio_map_cell_size_m: 4,
  radio_map_diffraction_enabled: true,
  radio_map_max_depth: 5,
  radio_map_samples_per_tx: 300000,
  radio_map_plane_height_m: 1.5,
  radio_map_min_extent_m: 150,
};

const DEFAULT_SUBSCRIBER_MARKER_SETTINGS: SubscriberMarkerVisualizationSettings = {
  asset_name: 'subscriber_handset.glb',
  scale_x_m: 1.2,
  scale_y_m: 1.2,
  scale_z_m: 1.2,
  show_all_by_default: true,
};

const DEFAULT_STAGE_A_GUARDRAIL_SETTINGS: StageAGuardrailSettings = {
  policy_mode: 'deny',
  runpod_serverless: {
    enabled: false,
    max_estimated_total_vram_gb: 20,
    pathsolver_complexity_limit: 20000000,
    max_subscribers_no_batch: 2000,
    max_face_count_total: 900000,
    max_scene_dict_pickle_bytes: 134217728,
    max_bbox_area_m2: 4000000,
    recommended_subscriber_batch_size: 500,
  },
};

function normalizeStageAGuardrailSettings(
  settings: Partial<StageAGuardrailSettings> | null | undefined,
): StageAGuardrailSettings {
  return {
    ...DEFAULT_STAGE_A_GUARDRAIL_SETTINGS,
    ...settings,
    runpod_serverless: {
      ...DEFAULT_STAGE_A_GUARDRAIL_SETTINGS.runpod_serverless,
      ...(settings?.runpod_serverless ?? {}),
    },
  };
}

@Component({
  selector: 'app-app-settings-dialog',
  imports: [FormField, ConfirmDialogComponent],
  templateUrl: './app-settings-dialog.html',
  styleUrl: './app-settings-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.aria-hidden]': '!open()',
  },
})
export class AppSettingsDialog {
  private static readonly VERIZON_SYNTH_PREPOST_DATA_SOURCE = 'verizon_synth_prepost';
  private static readonly CENSUS_DEFAULT_VINTAGE = 2020;

  private readonly appSettings = inject(AppSettingsService);
  private readonly jobs = inject(SionnaJobService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly visualizationFirstField = viewChild<ElementRef<HTMLInputElement>>('visualizationFirstField');
  private readonly simulationFirstField = viewChild<ElementRef<HTMLInputElement>>('simulationFirstField');

  readonly open = input(false);
  readonly closed = output<void>();

  readonly sections = [
    { id: 'visualization', label: 'Visualization', description: 'Scene rendering and lobe behavior' },
    { id: 'simulation', label: 'Simulation', description: 'Shared radio-map and noise defaults' },
    { id: 'data', label: 'Data', description: 'Reserved for future ingest and source settings' },
  ] as const;

  readonly activeSection = signal<(typeof this.sections)[number]['id']>('visualization');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly nukingDataSource = signal(false);
  readonly checkingPurgeStatus = signal(false);
  readonly purgeConfirmOpen = signal(false);
  readonly purgeStatus = signal<CellTowerDataSourcePurgeStatus | null>(null);
  readonly jobsCleanupRetentionOptions: Array<{ value: JobCleanupRetention; label: string }> = [
    { value: '1w', label: 'Older than 1 week' },
    { value: '2w', label: 'Older than 2 weeks' },
    { value: '1m', label: 'Older than 1 month' },
  ];
  readonly jobsCleanupRetention = signal<JobCleanupRetention>('1w');
  readonly checkingJobsCleanupEstimate = signal(false);
  readonly jobsCleanupEstimate = signal<JobCleanupEstimate | null>(null);
  readonly nukeJobsConfirmOpen = signal(false);
  readonly nukingOldJobs = signal(false);
  readonly censusStateCode = signal('');
  readonly censusSubmitting = signal(false);
  readonly censusJobId = signal<string | null>(null);
  readonly censusJobStatus = signal<JobStatus | null>(null);
  readonly censusResultSummary = signal<string | null>(null);
  readonly loadError = signal<string | null>(null);
  readonly saveError = signal<string | null>(null);
  readonly saveNotice = signal<string | null>(null);
  readonly savedSettings = signal<AntennaLobeVisualizationSettings>(DEFAULT_ANTENNA_LOBE_SETTINGS);
  readonly settingsModel = signal<AntennaLobeVisualizationSettings>(DEFAULT_ANTENNA_LOBE_SETTINGS);
  readonly savedSimulationSettings = signal<SimulationSettings>(DEFAULT_SIMULATION_SETTINGS);
  readonly simulationSettingsModel = signal<SimulationSettings>(DEFAULT_SIMULATION_SETTINGS);
  readonly savedStageAGuardrailSettings = signal<StageAGuardrailSettings>(DEFAULT_STAGE_A_GUARDRAIL_SETTINGS);
  readonly stageAGuardrailSettingsModel = signal<StageAGuardrailSettings>(DEFAULT_STAGE_A_GUARDRAIL_SETTINGS);
  readonly savedSubscriberMarkerSettings = signal<SubscriberMarkerVisualizationSettings>(DEFAULT_SUBSCRIBER_MARKER_SETTINGS);
  readonly subscriberMarkerSettingsModel = signal<SubscriberMarkerVisualizationSettings>(DEFAULT_SUBSCRIBER_MARKER_SETTINGS);
  readonly availableModels = signal<AvailableModelSummary[]>([]);
  readonly settingsForm = form(this.settingsModel, (settings) => {
    required(settings.minimum_boresight_length_m);
    required(settings.maximum_boresight_length_m);
    required(settings.maximum_ground_touch_length_m);
    required(settings.minimum_power_dbm);
    required(settings.maximum_power_dbm);
    required(settings.horizontal_width_ratio);
    required(settings.vertical_width_ratio);
    required(settings.ground_clearance_m);

    min(settings.minimum_boresight_length_m, 0.1);
    min(settings.maximum_boresight_length_m, 0.1);
    min(settings.maximum_ground_touch_length_m, 0.1);
    min(settings.horizontal_width_ratio, 0.001);
    min(settings.vertical_width_ratio, 0.001);
    min(settings.ground_clearance_m, 0);
    max(settings.horizontal_width_ratio, 1);
    max(settings.vertical_width_ratio, 1);

    validate(settings.maximum_boresight_length_m, ({ value, valueOf }) =>
      value() < valueOf(settings.minimum_boresight_length_m)
        ? {
            kind: 'range',
            message: 'Maximum boresight length must be greater than or equal to minimum boresight length.',
          }
        : undefined,
    );
    validate(settings.maximum_ground_touch_length_m, ({ value, valueOf }) =>
      value() < valueOf(settings.maximum_boresight_length_m)
        ? {
            kind: 'range',
            message: 'Maximum ground-touch length must be greater than or equal to maximum boresight length.',
          }
        : undefined,
    );
    validate(settings.maximum_power_dbm, ({ value, valueOf }) =>
      value() <= valueOf(settings.minimum_power_dbm)
        ? {
            kind: 'range',
            message: 'Maximum power must be greater than minimum power.',
          }
        : undefined,
    );
  });
  readonly simulationSettingsForm = form(this.simulationSettingsModel, (settings) => {
    required(settings.radio_map_frequency_ghz);
    required(settings.radio_map_bandwidth_hz);
    required(settings.radio_map_temperature_k);
    required(settings.radio_map_receiver_noise_figure_db);
    required(settings.radio_map_cell_size_m);
    required(settings.radio_map_max_depth);
    required(settings.radio_map_samples_per_tx);
    required(settings.radio_map_plane_height_m);
    required(settings.radio_map_min_extent_m);

    min(settings.radio_map_frequency_ghz, 1);
    max(settings.radio_map_frequency_ghz, 100);
    min(settings.radio_map_bandwidth_hz, 100000);
    max(settings.radio_map_bandwidth_hz, 100000000);
    min(settings.radio_map_temperature_k, 200);
    max(settings.radio_map_temperature_k, 400);
    min(settings.radio_map_receiver_noise_figure_db, 0);
    max(settings.radio_map_receiver_noise_figure_db, 20);
    min(settings.radio_map_cell_size_m, 0.1);
    min(settings.radio_map_max_depth, 1);
    max(settings.radio_map_max_depth, 12);
    min(settings.radio_map_samples_per_tx, 1000);
    min(settings.radio_map_plane_height_m, 0);
    min(settings.radio_map_min_extent_m, 1);
  });
  readonly subscriberMarkerSettingsForm = form(this.subscriberMarkerSettingsModel, (settings) => {
    required(settings.scale_x_m);
    required(settings.scale_y_m);
    required(settings.scale_z_m);

    min(settings.scale_x_m, 0.01);
    min(settings.scale_y_m, 0.01);
    min(settings.scale_z_m, 0.01);
  });

  readonly sectionHasForm = computed(() => this.activeSection() !== 'data');
  readonly stageAGuardrailValidationError = computed(() => {
    const settings = this.stageAGuardrailSettingsModel().runpod_serverless;
    if (settings.max_estimated_total_vram_gb <= 0) {
      return 'Max estimated VRAM must be greater than 0 GB.';
    }
    if (settings.pathsolver_complexity_limit < 1) {
      return 'PathSolver complexity limit must be at least 1.';
    }
    if (settings.max_subscribers_no_batch < 1) {
      return 'Max subscribers without batching must be at least 1.';
    }
    if (settings.max_face_count_total < 1) {
      return 'Max face count must be at least 1.';
    }
    if (settings.max_scene_dict_pickle_bytes < 1) {
      return 'Max scene dict pickle bytes must be at least 1.';
    }
    if (settings.max_bbox_area_m2 <= 0) {
      return 'Max bounding-box area must be greater than 0 m^2.';
    }
    if (settings.recommended_subscriber_batch_size < 1) {
      return 'Recommended subscriber batch size must be at least 1.';
    }
    return null;
  });
  readonly censusStateCodeError = computed(() => {
    const code = this.censusStateCode().trim().toUpperCase();
    if (!code) {
      return 'Enter a two-letter US state code.';
    }
    if (!/^[A-Z]{2}$/.test(code)) {
      return 'Use a two-letter US state code such as MA, TX, or NC.';
    }
    return null;
  });
  readonly censusJobStatusLabel = computed(() => {
    const status = this.censusJobStatus();
    if (!status) {
      return null;
    }
    const progress = Number.isFinite(status.progress) ? `${Math.round(status.progress * 100)}%` : null;
    const message = status.error || status.message || null;
    return [status.status.toUpperCase(), progress, message].filter(Boolean).join(' · ');
  });
  readonly footerNote = computed(() => {
    if (this.activeSection() === 'simulation') {
      return 'Saved values are global. Future baseline and scenario runs will use the shared simulation defaults, and runpod_serverless Stage A guardrails will use the updated limits.';
    }
    if (this.activeSection() === 'data') {
      return 'Use this section for admin ingest and cleanup tasks. Census ingest supports 2020 decennial state blocks; job cleanup removes old terminal jobs not protected by QoS scenario/project chains.';
    }
    return 'Saved values are global. Regenerate a scene after changing visualization settings.';
  });
  readonly jobsCleanupConfirmMessage = computed(() => {
    const estimate = this.jobsCleanupEstimate();
    if (!estimate) {
      return 'Nuke old jobs for the selected retention window?';
    }
    const targetScope = estimate.backend_scope === 'local' ? 'local backend' : 'remote backend';
    const targetDb = estimate.database_target || 'unknown database target';
    const targetApi = estimate.backend_api_origin || 'unknown API origin';
    const targetBucket = estimate.garage_bucket || 'unknown Garage bucket';
    const garagePart = estimate.garage_configured
      ? ` including ${estimate.garage_reclaimable_human || '0 B'} in Garage objects`
      : '';
    return `Target: ${targetScope} at ${targetApi}, DB ${targetDb}, Garage bucket ${targetBucket}. Delete ${estimate.deletable_jobs} old job(s) and up to ${estimate.estimated_reclaimable_human}${garagePart}? Jobs in scenario/project chains are protected.`;
  });
  readonly jobsCleanupTargetLabel = computed(() => {
    const estimate = this.jobsCleanupEstimate();
    if (!estimate) {
      return null;
    }
    const scope = estimate.backend_scope === 'local' ? 'Local backend' : 'Remote backend';
    const api = estimate.backend_api_origin || 'unknown API origin';
    const db = estimate.database_target || 'unknown database target';
    const bucket = estimate.garage_bucket || 'unknown Garage bucket';
    return `${scope} | API ${api} | DB ${db} | Garage bucket ${bucket}`;
  });
  readonly formInvalid = computed(() => {
    if (this.activeSection() === 'simulation') {
      return this.simulationSettingsForm().invalid() || this.stageAGuardrailValidationError() !== null;
    }
    if (this.activeSection() === 'visualization') {
      return this.settingsForm().invalid() || this.subscriberMarkerSettingsForm().invalid();
    }
    return false;
  });
  readonly hasUnsavedChanges = computed(() => {
    if (this.activeSection() === 'simulation') {
      return (
        JSON.stringify(this.simulationSettingsModel()) !==
          JSON.stringify(this.savedSimulationSettings()) ||
        JSON.stringify(this.stageAGuardrailSettingsModel()) !==
          JSON.stringify(this.savedStageAGuardrailSettings())
      );
    }
    if (this.activeSection() === 'visualization') {
      return (
        JSON.stringify(this.settingsModel()) !== JSON.stringify(this.savedSettings()) ||
        JSON.stringify(this.subscriberMarkerSettingsModel()) !== JSON.stringify(this.savedSubscriberMarkerSettings())
      );
    }
    return false;
  });

  constructor() {
    effect(() => {
      if (!this.open()) {
        return;
      }
      this.activeSection.set('visualization');
      void this.loadSettings();
    });
  }

  selectSection(sectionId: (typeof this.sections)[number]['id']): void {
    this.activeSection.set(sectionId);
  }

  close(): void {
    if (
      this.saving() ||
      this.nukingDataSource() ||
      this.checkingPurgeStatus() ||
      this.checkingJobsCleanupEstimate() ||
      this.nukingOldJobs()
    ) {
      return;
    }
    this.purgeConfirmOpen.set(false);
    this.nukeJobsConfirmOpen.set(false);
    this.saveError.set(null);
    this.saveNotice.set(null);
    this.closed.emit();
  }

  resetDraft(): void {
    if (this.activeSection() === 'simulation') {
      this.simulationSettingsModel.set({ ...this.savedSimulationSettings() });
      this.stageAGuardrailSettingsModel.set({
        ...this.savedStageAGuardrailSettings(),
        runpod_serverless: { ...this.savedStageAGuardrailSettings().runpod_serverless },
      });
    } else if (this.activeSection() === 'visualization') {
      this.settingsModel.set({ ...this.savedSettings() });
      this.subscriberMarkerSettingsModel.set({ ...this.savedSubscriberMarkerSettings() });
    }
    this.saveError.set(null);
    this.saveNotice.set(null);
  }

  async save(): Promise<void> {
    this.saveError.set(null);
    this.saveNotice.set(null);

    if (!this.sectionHasForm()) {
      return;
    }

    if (this.activeSection() === 'simulation') {
      if (this.stageAGuardrailValidationError()) {
        this.saveError.set(this.stageAGuardrailValidationError());
        return;
      }
      await submit(this.simulationSettingsForm, async () => {
        this.saving.set(true);
        try {
          const [response, guardrailResponse] = await Promise.all([
            firstValueFrom(this.appSettings.updateSimulationSettings(this.simulationSettingsModel())),
            firstValueFrom(this.appSettings.updateStageAGuardrailSettings(this.stageAGuardrailSettingsModel())),
          ]);
          const next = response.settings;
          this.savedSimulationSettings.set(next);
          this.simulationSettingsModel.set({ ...next });
          const nextGuardrails = normalizeStageAGuardrailSettings(guardrailResponse.settings);
          this.savedStageAGuardrailSettings.set(nextGuardrails);
          this.stageAGuardrailSettingsModel.set({
            ...nextGuardrails,
            runpod_serverless: { ...nextGuardrails.runpod_serverless },
          });
          this.saveNotice.set('Simulation defaults and Stage A runpod guardrails saved. Future jobs will pick up the new worker limits.');
        } catch (error) {
          this.saveError.set(this.formatError(error, 'Failed to save settings.'));
        } finally {
          this.saving.set(false);
        }
        return undefined;
      });
      return;
    }

    await submit(this.settingsForm, async () => {
      this.saving.set(true);
      try {
        const [response, subscriberMarkerResponse] = await Promise.all([
          firstValueFrom(this.appSettings.updateAntennaLobeVisualizationSettings(this.settingsModel())),
          firstValueFrom(this.appSettings.updateSubscriberMarkerVisualizationSettings(this.subscriberMarkerSettingsModel())),
        ]);
        const next = response.settings;
        this.savedSettings.set(next);
        this.settingsModel.set({ ...next });
        const nextSubscriberMarker = subscriberMarkerResponse.settings;
        this.savedSubscriberMarkerSettings.set(nextSubscriberMarker);
        this.subscriberMarkerSettingsModel.set({ ...nextSubscriberMarker });
        this.saveNotice.set('Visualization settings saved. Regenerate a scene to apply new lobe and subscriber marker assets.');
      } catch (error) {
        this.saveError.set(this.formatError(error, 'Failed to save settings.'));
      } finally {
        this.saving.set(false);
      }
      return undefined;
    });
  }

  async openPurgeVerizonSynthPrepostConfirm(): Promise<void> {
    if (this.nukingDataSource() || this.checkingPurgeStatus()) {
      return;
    }

    this.saveError.set(null);
    this.saveNotice.set(null);
    this.checkingPurgeStatus.set(true);
    try {
      const status = await firstValueFrom(
        this.appSettings.getCellTowerDataSourcePurgeStatus(AppSettingsDialog.VERIZON_SYNTH_PREPOST_DATA_SOURCE),
      );
      this.purgeStatus.set(status);
      if (!status.can_purge) {
        const projectList = status.blocking_projects
          .slice(0, 5)
          .map((project) => `${project.name} (#${project.id})`)
          .join(', ');
        const moreCount = status.blocked_project_count - Math.min(status.blocking_projects.length, 5);
        const moreLabel = moreCount > 0 ? `, and ${moreCount} more` : '';
        this.saveError.set(
          `Delete the referencing QoS projects first before purging verizon_synth_prepost. Blocking projects: ${projectList}${moreLabel}.`,
        );
        return;
      }
      this.purgeConfirmOpen.set(true);
    } catch (error) {
      this.saveError.set(this.formatError(error, 'Failed to inspect Verizon synth pre/post lineage.'));
    } finally {
      this.checkingPurgeStatus.set(false);
    }
  }

  cancelPurgeVerizonSynthPrepost(): void {
    this.purgeConfirmOpen.set(false);
  }

  updateJobsCleanupRetention(value: string): void {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized !== '1w' && normalized !== '2w' && normalized !== '1m') {
      return;
    }
    this.jobsCleanupRetention.set(normalized);
    this.jobsCleanupEstimate.set(null);
    this.nukeJobsConfirmOpen.set(false);
  }

  async refreshJobsCleanupEstimate(): Promise<void> {
    if (this.checkingJobsCleanupEstimate() || this.nukingOldJobs()) {
      return;
    }

    this.saveError.set(null);
    this.saveNotice.set(null);
    this.checkingJobsCleanupEstimate.set(true);
    try {
      const estimate = await firstValueFrom(
        this.appSettings.getJobsCleanupEstimate(this.jobsCleanupRetention()),
      );
      this.jobsCleanupEstimate.set(estimate);
      if (estimate.deletable_jobs > 0) {
        const garageInfo = estimate.garage_configured
          ? ` Garage: ${estimate.garage_reclaimable_human || '0 B'} across ${estimate.garage_object_count || 0} object(s).`
          : ` ${estimate.garage_configuration_message || 'Garage S3 is not configured on this backend process.'} Cleanup will be blocked until Garage is configured.`;
        this.saveNotice.set(
          `Found ${estimate.deletable_jobs} deletable job(s), estimate ${estimate.estimated_reclaimable_human} reclaimable.${garageInfo}`,
        );
      } else {
        this.saveNotice.set('No deletable jobs matched the selected retention window.');
      }
    } catch (error) {
      this.saveError.set(this.formatError(error, 'Failed to estimate old-job cleanup.'));
    } finally {
      this.checkingJobsCleanupEstimate.set(false);
    }
  }

  async openNukeOldJobsConfirm(): Promise<void> {
    if (this.checkingJobsCleanupEstimate() || this.nukingOldJobs()) {
      return;
    }

    this.nukeJobsConfirmOpen.set(false);
    await this.refreshJobsCleanupEstimate();
    const estimate = this.jobsCleanupEstimate();
    if (!estimate || estimate.deletable_jobs < 1) {
      return;
    }
    this.nukeJobsConfirmOpen.set(true);
  }

  cancelNukeOldJobs(): void {
    this.nukeJobsConfirmOpen.set(false);
  }

  async nukeOldJobs(): Promise<void> {
    if (this.nukingOldJobs()) {
      return;
    }

    this.nukeJobsConfirmOpen.set(false);
    this.saveError.set(null);
    this.saveNotice.set(null);
    this.nukingOldJobs.set(true);
    try {
      const result = await firstValueFrom(
        this.appSettings.cleanupOldJobs(this.jobsCleanupRetention()),
      );
      this.handleJobsCleanupResult(result);
      await this.refreshJobsCleanupEstimate();
    } catch (error) {
      this.saveError.set(this.formatError(error, 'Failed to cleanup old jobs.'));
    } finally {
      this.nukingOldJobs.set(false);
    }
  }

  private handleJobsCleanupResult(result: JobCleanupResult): void {
    if (!result.deleted) {
      this.saveNotice.set(result.message || 'No eligible jobs matched the selected retention window.');
      return;
    }

    const protectedCount =
      typeof result.protected_referenced_jobs === 'number' && Number.isFinite(result.protected_referenced_jobs)
        ? result.protected_referenced_jobs
        : 0;
    const garageDeletedObjects =
      typeof result.garage_deleted_objects === 'number' && Number.isFinite(result.garage_deleted_objects)
        ? result.garage_deleted_objects
        : 0;
    const garageSummary = result.garage_configured
      ? ` Garage: ${result.garage_reclaimed_human || '0 B'} across ${garageDeletedObjects} object(s).`
      : ' Garage not configured.';
    this.saveNotice.set(
      `Deleted ${result.deleted_jobs} job row(s) and ${result.deleted_job_dirs} job director${result.deleted_job_dirs === 1 ? 'y' : 'ies'} (${result.reclaimed_human} reclaimed).${garageSummary} Protected chain jobs: ${protectedCount}.`,
    );
  }

  updateCensusStateCode(value: string): void {
    this.censusStateCode.set(value.trim().toUpperCase());
  }

  toggleStageAGuardrailRunpodEnabled(checked: boolean): void {
    const current = this.stageAGuardrailSettingsModel();
    this.stageAGuardrailSettingsModel.set({
      ...current,
      runpod_serverless: {
        ...current.runpod_serverless,
        enabled: checked,
      },
    });
  }

  updateStageAGuardrailRunpodNumber(
    key:
      | 'max_estimated_total_vram_gb'
      | 'pathsolver_complexity_limit'
      | 'max_subscribers_no_batch'
      | 'max_face_count_total'
      | 'max_scene_dict_pickle_bytes'
      | 'max_bbox_area_m2'
      | 'recommended_subscriber_batch_size',
    value: number,
  ): void {
    const current = this.stageAGuardrailSettingsModel();
    this.stageAGuardrailSettingsModel.set({
      ...current,
      runpod_serverless: {
        ...current.runpod_serverless,
        [key]: value,
      },
    });
  }

  async ingestCensusState(): Promise<void> {
    if (this.censusSubmitting() || this.censusStateCodeError()) {
      return;
    }

    this.saveError.set(null);
    this.saveNotice.set(null);
    this.censusResultSummary.set(null);
    this.censusJobStatus.set(null);
    this.censusSubmitting.set(true);

    try {
      const response = await firstValueFrom(
        this.appSettings.submitCensusUsIngestState({
          state: this.censusStateCode().trim().toUpperCase(),
          vintage: AppSettingsDialog.CENSUS_DEFAULT_VINTAGE,
          unit_level: 'block',
        }),
      );
      this.handleSubmittedCensusJob(response);
    } catch (error) {
      this.censusSubmitting.set(false);
      this.saveError.set(this.formatError(error, 'Failed to submit Census ingest job.'));
    }
  }

  private handleSubmittedCensusJob(response: CensusUsIngestStateResponse): void {
    this.censusJobId.set(response.job_id);
    this.saveNotice.set(`Census ingest job ${response.job_id.slice(0, 8)} submitted. Waiting for completion…`);

    this.jobs
      .watchJobUntilDone(response.job_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status) => {
          this.censusJobStatus.set(status);
          if (status.status === 'done') {
            this.jobs.getResult<{ result?: { rows_upserted?: number; coverage_summary?: { population_total?: number; row_count?: number } } }>(response.job_id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (resultEnvelope) => {
                  const result = resultEnvelope?.result ?? {};
                  const rowCount = result.coverage_summary?.row_count ?? result.rows_upserted ?? 0;
                  const population = result.coverage_summary?.population_total ?? 0;
                  this.censusResultSummary.set(`Ingested ${rowCount.toLocaleString()} census blocks and ${population.toLocaleString()} total population.`);
                  this.saveNotice.set(`Census ingest job ${response.job_id.slice(0, 8)} completed.`);
                  this.censusSubmitting.set(false);
                },
                error: (error) => {
                  this.saveError.set(this.formatError(error, 'Census ingest completed, but the result artifact could not be loaded.'));
                  this.censusSubmitting.set(false);
                },
              });
            return;
          }

          if (status.status === 'error' || status.status === 'canceled') {
            this.saveError.set(status.error || status.message || 'Census ingest job did not complete successfully.');
            this.censusSubmitting.set(false);
          }
        },
        error: (error) => {
          this.saveError.set(this.formatError(error, 'Failed while polling Census ingest job status.'));
          this.censusSubmitting.set(false);
        },
      });
  }

  async purgeVerizonSynthPrepost(): Promise<void> {
    if (this.nukingDataSource()) {
      return;
    }

    this.purgeConfirmOpen.set(false);
    this.saveError.set(null);
    this.saveNotice.set(null);
    this.nukingDataSource.set(true);
    try {
      const response = await firstValueFrom(
        this.appSettings.purgeCellTowerDataSource(AppSettingsDialog.VERIZON_SYNTH_PREPOST_DATA_SOURCE),
      );
      this.saveNotice.set(
        response.deleted_cell_towers > 0
          ? `Deleted ${response.deleted_cell_towers} cell_towers row(s) for ${response.data_source}.`
          : `No cell_towers rows matched ${response.data_source}.`,
      );
      this.purgeStatus.set(null);
    } catch (error) {
      this.saveError.set(this.formatError(error, 'Failed to purge Verizon synth pre/post lineage.'));
    } finally {
      this.nukingDataSource.set(false);
    }
  }

  private async loadSettings(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    this.saveError.set(null);
    this.saveNotice.set(null);
    try {
      const [visualizationResponse, simulationResponse, stageAGuardrailResponse, subscriberMarkerResponse, models] = await Promise.all([
        firstValueFrom(this.appSettings.getAntennaLobeVisualizationSettings()),
        firstValueFrom(this.appSettings.getSimulationSettings()),
        firstValueFrom(this.appSettings.getStageAGuardrailSettings()),
        firstValueFrom(this.appSettings.getSubscriberMarkerVisualizationSettings()),
        firstValueFrom(this.appSettings.getAvailableModels()),
      ]);
      const nextVisualization = visualizationResponse.settings;
      this.savedSettings.set(nextVisualization);
      this.settingsModel.set({ ...nextVisualization });
      const nextSimulation = simulationResponse.settings;
      this.savedSimulationSettings.set(nextSimulation);
      this.simulationSettingsModel.set({ ...nextSimulation });
      const nextGuardrails = normalizeStageAGuardrailSettings(stageAGuardrailResponse.settings);
      this.savedStageAGuardrailSettings.set(nextGuardrails);
      this.stageAGuardrailSettingsModel.set({
        ...nextGuardrails,
        runpod_serverless: { ...nextGuardrails.runpod_serverless },
      });
      const nextSubscriberMarker = subscriberMarkerResponse.settings;
      this.savedSubscriberMarkerSettings.set(nextSubscriberMarker);
      this.subscriberMarkerSettingsModel.set({ ...nextSubscriberMarker });
      this.availableModels.set(
        models
          .filter((model) => model.type === 'glb' || model.type === 'gltf')
          .sort((left, right) => left.name.localeCompare(right.name)),
      );
      queueMicrotask(() => {
        if (this.activeSection() === 'simulation') {
          this.simulationFirstField()?.nativeElement.focus();
          return;
        }
        this.visualizationFirstField()?.nativeElement.focus();
      });
    } catch (error) {
      this.availableModels.set([]);
      this.loadError.set(this.formatError(error, 'Failed to load settings.'));
    } finally {
      this.loading.set(false);
    }
  }

  private formatError(error: unknown, fallback: string): string {
    if (typeof error === 'object' && error !== null) {
      const detail = this.extractErrorDetail(error);
      if (detail) {
        return detail;
      }
      if ('message' in error && typeof error.message === 'string') {
        return error.message;
      }
    }
    return fallback;
  }

  private extractErrorDetail(error: unknown): string | null {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }
    if (typeof error !== 'object' || error === null) {
      return null;
    }

    if ('error' in error) {
      const nested = this.extractErrorDetail(error.error);
      if (nested) {
        return nested;
      }
    }

    if ('detail' in error) {
      const detail = error.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      if (typeof detail === 'object' && detail !== null && 'message' in detail && typeof detail.message === 'string') {
        return detail.message;
      }
    }

    if ('message' in error && typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return null;
  }

}
